import * as React from "react";
import { useLocation } from "wouter";
import { Zap, ArrowRight, ArrowLeft, RotateCcw } from "lucide-react";

/**
 * Keep It Up category landing — recurring maintenance that keeps the systematic
 * (Placement) state from drifting. One of the three top-level Organize categories:
 * Placement · Events · Keep It Up. The "keep up" half of the set-up → keep-up pair.
 *   - Routines    (repeat tidy-ups) → /organize/routines
 *   - Quick Reset (put a room back fast) → /organize/quick-reset
 */
type UpkeepCard = {
  key: string;
  label: string;
  hint: string;
  icon: React.ElementType;
  iconClass: string;
  assistive?: boolean;
  href: string;
};

const UPKEEP: UpkeepCard[] = [
  { key: "routines", label: "Routines", hint: "Keep it maintained with repeat tidy-ups", icon: Zap, iconClass: "text-primary", href: "/organize/routines" },
  { key: "quick-reset", label: "Quick Reset", hint: "Put a room back fast", icon: RotateCcw, iconClass: "text-sage-deep", assistive: true, href: "/organize/quick-reset" },
];

export default function KeepItUpPage() {
  const [, navigate] = useLocation();

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <header>
        <button
          onClick={() => navigate("/organize")}
          className="inline-flex items-center gap-1 text-sm text-faint hover:text-primary transition-colors mb-2"
        >
          <ArrowLeft className="w-4 h-4" />Organize
        </button>
        <div className="flex items-center gap-2 mb-1">
          <Zap className="w-5 h-5 text-primary" />
          <span className="text-sm font-medium text-primary">Keep it up</span>
        </div>
        <h1 className="text-4xl font-semibold text-ink font-headline tracking-[-0.02em]">Keep it up</h1>
        <p className="text-body mt-1 text-sm">Light, recurring upkeep so the order you set up stays put.</p>
      </header>

      <div className="space-y-3">
        {UPKEEP.map((u) => {
          const Icon = u.icon;
          return (
            <a
              key={u.key}
              href={u.href}
              className="flex items-center gap-3 bg-white rounded-lg border border-clay-border p-4 hover:border-primary/40 transition-colors group"
            >
              <div className={`w-10 h-10 rounded-md flex items-center justify-center shrink-0 ${u.assistive ? "bg-sage-wash" : "bg-amethyst-soft"}`}>
                <Icon className={`w-5 h-5 ${u.iconClass}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[1.0625rem] font-semibold text-ink">{u.label}</div>
                <div className="text-[0.8125rem] text-faint">{u.hint}</div>
              </div>
              <ArrowRight className="w-4 h-4 text-faint group-hover:text-primary transition-colors shrink-0" />
            </a>
          );
        })}
      </div>
    </div>
  );
}
